import { normalizedUuidSchema, type PlaceOrderInput } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router';
import { orderPlacementBlock, useOrderPlacementQuery } from '@/entities/order';
import { usePlaceOrder } from '@/features/order-place';
import { env, MY_QUOTES_PATH, myQuoteDetailPath } from '@/shared/config';
import { Button, buttonVariants, cn, EyebrowLabel, SitePageShell } from '@/shared/ui';
import { OrderPlacement } from '@/widgets/order-placement';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

const REISSUED_NOTICE = '견적이 다시 발행되었습니다. 최신 견적서를 확인하고 다시 동의해 주세요.';

/** 열 수 없는 화면(못 찾음·준비 중·주문 불가) — 내 견적 상세의 못 찾음과 같은 얇은 위아래 선 사이 가운데 안내. */
const BLOCK_CLASS =
  'flex flex-col items-center gap-6 border-y border-foreground/10 py-24 text-center';
const BLOCK_TITLE_CLASS = 'text-2xl font-bold tracking-[-0.02em] sm:text-3xl';
/** 안내 칸의 이동 링크 — 보조 버튼 모양(얇은 테두리), 휴대폰에서도 44px 이상. */
const BLOCK_LINK_CLASS = cn(
  buttonVariants({ variant: 'outline', size: 'lg' }),
  'h-12 border-foreground/20 px-8',
);

/**
 * 주문하기 페이지(견적 설계 §7-4·§7-5, 주문 설계 §8-1). 주문하기 gate(`VITE_ORDER_PLACEMENT_ENABLED`, 기본 닫힘 — R17
 * 사용자 확인 전)가 닫혀 있으면 조회 없이 준비 중 안내만 낸다. 열려 있으면 주문하기 화면 데이터(`orders.getPlacement` — 최신
 * 발행본·견적 배송지·별첨 고지)를 위젯에 넘기고, 제출은 `orders.place` 뒤 결제 페이지로 간다. 서버가 발행 설정(별첨 고지)이
 * 없어 거절하면 같은 준비 중 안내다(서버 gate). 최신 발행본·만료·상품 가용성은 제출 때 서버가 잠금 아래에서 다시 검사한다.
 * 제출 사이에 견적이 다시 발행되면 위젯이 새 발행본으로 다시 그려지므로(두 동의는 새 문서에 다시 받는다) 마지막으로 제출한
 * 배송지 주소들을 이어 쓰도록 넘기고 다시 발행되었다는 안내를 보인다.
 * 머리는 내 견적 상세와 같은 결(견적으로 돌아가는 링크·작은 영문 머리말·큰 굵은 제목·얇은 아랫선)이다.
 */
export function OrderPlacePage(): ReactElement {
  const { quoteRequestId = '' } = useParams();
  const parsed = normalizedUuidSchema.safeParse(quoteRequestId);
  const open = env.VITE_ORDER_PLACEMENT_ENABLED;
  const query = useOrderPlacementQuery(parsed.success ? parsed.data : '', {
    enabled: open && parsed.success,
  });
  const placing = usePlaceOrder();
  const [submitted, setSubmitted] = useState<PlaceOrderInput | null>(null);
  const block = orderPlacementBlock(query.error);
  const quotePath = myQuoteDetailPath(quoteRequestId);
  const backToQuote = (
    <Link to={quotePath} className={BLOCK_LINK_CLASS}>
      내 견적으로
    </Link>
  );

  let body: ReactElement;
  if (!parsed.success || block === 'NOT_FOUND') {
    body = (
      <section className={BLOCK_CLASS}>
        <div className="flex flex-col gap-3">
          <h1 className={BLOCK_TITLE_CLASS}>견적을 찾을 수 없습니다.</h1>
          <p className="text-sm text-muted-foreground">
            삭제됐거나 다른 계정의 견적, 혹은 잘못된 주소일 수 있습니다.
          </p>
        </div>
        <Link to={MY_QUOTES_PATH} className={BLOCK_LINK_CLASS}>
          내 견적 목록으로
        </Link>
      </section>
    );
  } else if (!open || block === 'NOT_READY') {
    body = (
      <section className={BLOCK_CLASS}>
        <h1 className={BLOCK_TITLE_CLASS}>주문 기능 준비 중</h1>
        {backToQuote}
      </section>
    );
  } else if (block === 'UNAVAILABLE') {
    body = (
      <section className={BLOCK_CLASS}>
        <div className="flex flex-col gap-3">
          <h1 className={BLOCK_TITLE_CLASS}>주문할 수 없는 견적입니다.</h1>
          <p className="text-sm text-muted-foreground">
            유효기한이 지났거나 주문할 수 있는 상태가 아닙니다. 내 견적에서 상태를 확인해 주세요.
          </p>
        </div>
        {backToQuote}
      </section>
    );
  } else if (query.isPending) {
    // 주문하기 화면과 같은 두 칸 자리표시 — 문구는 화면낭독기에만 읽힌다.
    body = (
      <div>
        <p role="status" className="sr-only">
          견적을 불러오는 중입니다.
        </p>
        <div aria-hidden className="flex flex-col gap-10">
          <div className="h-24 w-full max-w-md animate-pulse bg-muted/70" />
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16">
            <div className="min-h-96 animate-pulse bg-muted/70" />
            <div className="min-h-64 animate-pulse bg-muted/70" />
          </div>
        </div>
      </div>
    );
  } else if (query.isError) {
    body = (
      <div
        role="alert"
        className="flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center"
      >
        <p className="text-sm text-destructive">견적을 불러오지 못했습니다.</p>
        <Button
          variant="outline"
          className="h-11 border-foreground/20 px-6"
          onClick={() => {
            void query.refetch();
          }}
        >
          다시 시도
        </Button>
      </div>
    );
  } else {
    const { version, destinations, withdrawalNotice } = query.data;
    const reissued = submitted !== null && submitted.versionId !== version.id;
    body = (
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-8">
          <Link
            to={quotePath}
            className="inline-flex min-h-11 w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0"
          >
            <ArrowLeft aria-hidden className="size-4" strokeWidth={1.5} />
            견적으로 돌아가기
          </Link>
          <div className="flex flex-col gap-5 border-b border-foreground/10 pb-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
            <div className="flex flex-col gap-4">
              <EyebrowLabel>Order</EyebrowLabel>
              <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
                주문하기
              </h1>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground lg:max-w-xl lg:pb-1 lg:text-right">
              발행된 견적 내용을 확인하고 배송지와 동의 항목을 입력해 주세요. 주문하면 결제 화면으로
              이어집니다.
            </p>
          </div>
        </header>
        <OrderPlacement
          key={`${parsed.data}-${version.id}`}
          quoteRequestId={parsed.data}
          version={version}
          withdrawalNotice={withdrawalNotice}
          destinations={destinations}
          previousInput={submitted}
          onSubmit={(input) => {
            setSubmitted(input);
            return placing.place(input);
          }}
          pending={placing.isPending}
          error={reissued ? REISSUED_NOTICE : null}
        />
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      {body}
    </SitePageShell>
  );
}
